import React, { useEffect, useState } from 'react';
import { Loader2, CalendarRange } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const iso = (d) => d.toISOString().slice(0, 10);
const fyStart = (y) => `${y}-07-01`;
function presets() {
  const now = new Date();
  const y = now.getFullYear();
  const fy = now.getMonth() >= 6 ? y : y - 1;
  const ago = (n) => iso(new Date(now.getTime() - n * 864e5));
  return [
    { key: 'all', label: 'All time', from: '', to: '' },
    { key: '30d', label: 'Last 30 days', from: ago(30), to: iso(now) },
    { key: '90d', label: 'Last 90 days', from: ago(90), to: iso(now) },
    { key: 'fy', label: `FY ${fy}/${String(fy + 1).slice(2)}`, from: fyStart(fy), to: iso(now) },
    { key: 'lastfy', label: `FY ${fy - 1}/${String(fy).slice(2)}`, from: fyStart(fy - 1), to: `${fy}-06-30` },
  ];
}
const money = (v) => `$${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const pct = (v) => (v === null || v === undefined ? '—' : `${v}%`);

export function ShopAnalytics() {
  const { shopAnalytics } = useAuth();
  const [range, setRange] = useState({ key: 'all', from: '', to: '' });
  const [data, setData] = useState(null);
  const [err, setErr] = useState('');
  useEffect(() => {
    setData(null); setErr('');
    shopAnalytics(range.from, range.to).then(setData).catch((e) => setErr(e?.response?.data?.detail || 'Could not load analytics'));
    /* eslint-disable-next-line */
  }, [range.from, range.to]);
  const t = data?.totals || {};
  const input = 'px-2 py-1 rounded border text-xs';
  return (
    <div data-testid="shop-analytics">
      <div className="flex flex-wrap items-center gap-2 mb-3" data-testid="shop-analytics-range">
        <CalendarRange size={14} style={{ color: 'var(--olive)' }} />
        {presets().map((p) => (
          <button key={p.key} type="button" data-testid={`shop-range-${p.key}`} onClick={() => setRange(p)} className="px-2.5 py-1 rounded text-xs font-title"
            style={range.key === p.key ? { background: 'var(--navy)', color: 'var(--cream)' } : { background: 'white', border: '1px solid rgba(0,0,0,0.18)' }}>{p.label}</button>
        ))}
        <span className="text-xs" style={{ color: 'var(--olive)' }}>or</span>
        <input type="date" data-testid="shop-range-from" className={input} style={{ borderColor: 'rgba(0,0,0,0.2)' }} value={range.from} onChange={(e) => setRange({ key: 'custom', from: e.target.value, to: range.to })} />
        <span className="text-xs" style={{ color: 'var(--olive)' }}>to</span>
        <input type="date" data-testid="shop-range-to" className={input} style={{ borderColor: 'rgba(0,0,0,0.2)' }} value={range.to} onChange={(e) => setRange({ key: 'custom', from: range.from, to: e.target.value })} />
      </div>
      {err && <p className="text-sm" style={{ color: '#8a1f11' }} data-testid="shop-analytics-error">{err}</p>}
      {!data && !err && <div className="text-center py-6"><Loader2 className="animate-spin inline" size={16} /></div>}
      {data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-5 gap-2 mb-3" data-testid="shop-analytics-totals">
            {[['Views', t.views], ['Items sold', t.sold], ['Orders', t.orders], ['Revenue', money(t.revenue)], ['Conversion', pct(t.conversion)]].map(([l, v]) => (
              <div key={l} className="rounded p-2.5" style={{ background: 'rgba(0,0,0,0.03)' }}>
                <div className="text-[11px]" style={{ color: 'var(--olive)' }}>{l}</div>
                <div className="font-title text-lg" style={{ color: 'var(--navy)' }} data-testid={`shop-total-${l.toLowerCase().replace(/\s/g, '-')}`}>{v ?? 0}</div>
              </div>
            ))}
          </div>
          {range.from && data.view_history_from && new Date(data.view_history_from) > new Date(range.from) && (
            <p className="text-[11px] mb-2" style={{ color: 'var(--olive)' }} data-testid="shop-analytics-note">View history is recorded from {new Date(data.view_history_from).toLocaleDateString()}; earlier views are only in the all-time counters.</p>
          )}
          <div className="grid md:grid-cols-3 gap-4">
            <div className="md:col-span-2 bg-white rounded-lg shadow-sm border overflow-x-auto" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <table className="w-full text-sm" data-testid="shop-analytics-table">
                <thead><tr style={{ background: 'var(--navy)' }}>
                  {['Product', 'Views', 'Sold', 'Conversion', 'Orders', 'Revenue'].map((h, i) => <th key={h} className={`px-3 py-2 font-title ${i ? 'text-right' : 'text-left'}`} style={{ color: 'var(--cream)' }}>{h}</th>)}
                </tr></thead>
                <tbody>
                  {data.products.length === 0 ? <tr><td colSpan={6} className="px-3 py-4 text-center" style={{ color: 'var(--olive)' }}>No products.</td></tr>
                    : data.products.map((p) => (
                      <tr key={p.id} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }} data-testid={`shop-analytics-row-${p.id}`}>
                        <td className="px-3 py-2" style={{ color: 'var(--navy)' }}>{p.name}<div className="text-[11px]" style={{ color: 'var(--olive)' }}>{p.category}</div></td>
                        <td className="px-3 py-2 text-right">{p.views}</td>
                        <td className="px-3 py-2 text-right">{p.sold}</td>
                        <td className="px-3 py-2 text-right font-title" style={{ color: p.conversion ? '#1a7f37' : 'var(--olive)' }} data-testid={`shop-conversion-${p.id}`}>{pct(p.conversion)}</td>
                        <td className="px-3 py-2 text-right">{p.orders}</td>
                        <td className="px-3 py-2 text-right">{money(p.revenue)}</td>
                      </tr>
                    ))}
                </tbody>
              </table>
            </div>
            <div className="bg-white rounded-lg shadow-sm border p-3" style={{ borderColor: 'rgba(0,0,0,0.08)' }}>
              <h4 className="font-title text-sm mb-2" style={{ color: 'var(--navy)' }}>Sales by month</h4>
              {data.monthly.length === 0 ? <p className="text-sm" style={{ color: 'var(--olive)' }} data-testid="shop-monthly-empty">No paid orders in this period.</p> : (
                <table className="w-full text-sm" data-testid="shop-monthly-table">
                  <thead><tr style={{ color: 'var(--olive)' }}><th className="text-left font-title py-1">Month</th><th className="text-right font-title">Orders</th><th className="text-right font-title">Revenue</th></tr></thead>
                  <tbody>{data.monthly.map((m) => (
                    <tr key={m.month} className="border-t" style={{ borderColor: 'rgba(0,0,0,0.06)' }}><td className="py-1">{m.month}</td><td className="text-right">{m.orders}</td><td className="text-right font-title" style={{ color: 'var(--maroon)' }}>{money(m.revenue)}</td></tr>
                  ))}</tbody>
                </table>
              )}
            </div>
          </div>
        </>
      )}
    </div>
  );
}
